import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';
import CheckinQuestionsEditor from './CheckinQuestionsEditor.jsx';
import BodyMapInput from './BodyMapInput.jsx';
import { FACE_SCALE, bodymapLabels } from '../utils/bodyRegions.js';
import './AttendanceTracking.css';
import './CheckinQuestions.css';

const AttendanceTracking = ({ embedded = false }) => {
  const { user } = useAuth();
  const toast = useToast();
  const token = user?.token;
  const [searchParams] = useSearchParams();  // deep-link from "Today": ?class=<id>&date=<YYYY-MM-DD>
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedDate, setSelectedDate] = useState(searchParams.get('date') || new Date().toISOString().split('T')[0]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [reports, setReports] = useState([]);
  const [activeView, setActiveView] = useState('daily'); // daily, reports, makeup
  const [classSchedule, setClassSchedule] = useState([]);

  // Check-in wellness questions (synced from the admin editor)
  const [questions, setQuestions] = useState([]);      // active questions
  const [answered, setAnswered] = useState(new Set());  // athlete_ids answered on selectedDate
  const [gate, setGate] = useState(null);               // { athlete, answers } while answering
  const [showEditor, setShowEditor] = useState(false);
  const [responses, setResponses] = useState(null);     // coach view (null = hidden)
  const isAdmin = user?.role === 'admin';
  const authHeaders = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

  const loadQuestions = async () => {
    try {
      // Scope to the selected class so athletes only see questions assigned to
      // their class (plus team-wide ones). No class → all (management fallback).
      const url = selectedClass?.id
        ? `${API_BASE_URL}/api/checkin/questions?classId=${selectedClass.id}`
        : `${API_BASE_URL}/api/checkin/questions`;
      const r = await fetch(url, { headers: authHeaders });
      const data = r.ok ? await r.json() : [];
      setQuestions((Array.isArray(data) ? data : []).filter(q => q.active));
    } catch { setQuestions([]); }
  };
  useEffect(() => { loadQuestions(); /* eslint-disable-next-line */ }, [selectedClass]);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/checkin/answered?date=${selectedDate}`, { headers: authHeaders });
        const data = r.ok ? await r.json() : [];
        setAnswered(new Set(Array.isArray(data) ? data : []));
      } catch { setAnswered(new Set()); }
    })();
    /* eslint-disable-next-line */
  }, [selectedDate]);

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    if (selectedClass && activeView === 'daily') {
      fetchDailyAttendance();
      fetchSchedule();
    } else if (selectedClass && activeView === 'reports') {
      fetchAttendanceReports();
    }
  }, [selectedClass, selectedDate, activeView]);

  const fetchClasses = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/classes?active_only=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setClasses(data);
      if (data.length > 0) {
        // Honor a deep-link ?class=<id> from the Today page; else first class
        const wantId = searchParams.get('class');
        const target = wantId ? data.find(c => String(c.id) === String(wantId)) : null;
        setSelectedClass(target || data[0]);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchDailyAttendance = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/attendance/class/${selectedClass.id}/date/${selectedDate}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      setAttendanceData(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching attendance:', error);
      setAttendanceData([]);
    }
  };

  const fetchAttendanceReports = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/attendance/reports?class_id=${selectedClass.id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      setReports(data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    }
  };

  const fetchSchedule = async () => {
    if (!selectedClass) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/classes/${selectedClass.id}/schedule`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setClassSchedule(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching schedule:', error);
      setClassSchedule([]);
    }
  };

  const getUpcomingPractices = () => {
    if (!classSchedule || classSchedule.length === 0) return [];
    
    const practices = [];
    const today = new Date();
    const daysToShow = 14;
    
    for (let i = 0; i < daysToShow; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dayName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][date.getDay()];
      
      const scheduledPractice = classSchedule.find(s => s.day_of_week === dayName);
      if (scheduledPractice) {
        practices.push({
          date: date.toISOString().split('T')[0],
          dayName,
          start_time: scheduledPractice.start_time,
          end_time: scheduledPractice.end_time
        });
      }
    }
    return practices;
  };

  const markAttendance = async (athleteId, status) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/attendance/mark`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          class_id: selectedClass.id,
          athlete_id: athleteId,
          date: selectedDate,
          status: status,
          is_excused: status === 'absent' ? 0 : undefined
        })
      });

      if (response.ok) {
        fetchDailyAttendance();
      } else {
        toast.error('Failed to mark attendance');
      }
    } catch (error) {
      console.error('Error marking attendance:', error);
    }
  };

  const clearAttendance = async (athleteId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/attendance/clear`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          class_id: selectedClass.id,
          athlete_id: athleteId,
          date: selectedDate
        })
      });

      if (response.ok) {
        fetchDailyAttendance();
      } else {
        toast.error('Failed to clear attendance');
      }
    } catch (error) {
      console.error('Error clearing attendance:', error);
    }
  };

  const toggleCheckIn = (athlete) => {
    if (athlete.status === 'present') {
      // Already checked in, uncheck them
      clearAttendance(athlete.id);
    } else if (questions.length > 0 && !answered.has(athlete.id)) {
      // Checking in + they haven't answered the wellness questions today → gate first
      setGate({ athlete, answers: {} });
    } else {
      markAttendance(athlete.id, 'present');
    }
  };

  const setGateAnswer = (qid, value) =>
    setGate(g => ({ ...g, answers: { ...g.answers, [qid]: value } }));

  const submitGate = async () => {
    const athlete = gate.athlete;
    const answers = questions.map(q => ({ question_id: q.id, response: gate.answers[q.id] }));
    try {
      const r = await fetch(`${API_BASE_URL}/api/checkin/responses`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ athlete_id: athlete.id, date: selectedDate, answers })
      });
      if (!r.ok) throw new Error();
      setAnswered(s => new Set(s).add(athlete.id));
      setGate(null);
      await markAttendance(athlete.id, 'present'); // proceed with check-in
    } catch {
      toast.error('Could not save check-in');
    }
  };

  const loadResponses = async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/checkin/responses?date=${selectedDate}`, { headers: authHeaders });
      setResponses(r.ok ? await r.json() : []);
    } catch { setResponses([]); }
  };

  const markAllPresent = async () => {
    if (!window.confirm('Mark all athletes as present?')) return;

    for (const athlete of attendanceData) {
      if (!athlete.status) {
        await markAttendance(athlete.id, 'present');
      }
    }
    fetchDailyAttendance();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return '#4CAF50';
      case 'absent': return '#f44336';
      case 'late': return '#FF9800';
      case 'excused': return '#2196F3';
      default: return '#999';
    }
  };

  const getAttendanceRate = (rate) => {
    if (rate >= 90) return 'excellent';
    if (rate >= 75) return 'good';
    if (rate >= 60) return 'fair';
    return 'poor';
  };

  return (
    <div className="attendance-tracking">
      <div className="header">
        {!embedded && <h1>Attendance Tracking</h1>}
        <div className="view-switcher">
          <button
            className={activeView === 'daily' ? 'active' : ''}
            onClick={() => setActiveView('daily')}
          >
            Daily Check-In
          </button>
          <button
            className={activeView === 'reports' ? 'active' : ''}
            onClick={() => setActiveView('reports')}
          >
            Reports
          </button>
        </div>
        <div className="att-checkin-tools">
          <button className="att-tool" onClick={loadResponses} title="See today's check-in answers">📋 Check-in responses</button>
          {isAdmin && (
            <button className="att-tool" onClick={() => setShowEditor(true)} title="Edit the check-in questions">⚙️ Check-in questions</button>
          )}
        </div>
      </div>

      <div className="controls">
        <div className="class-selector">
          <label>Class:</label>
          <select
            value={selectedClass?.id || ''}
            onChange={(e) => {
              const cls = classes.find(c => c.id === parseInt(e.target.value));
              setSelectedClass(cls);
            }}
          >
            {classes.map((cls) => (
              <option key={cls.id} value={cls.id}>
                {cls.class_name} ({cls.level})
              </option>
            ))}
          </select>
        </div>

        {activeView === 'daily' && (
          <>
            <div className="date-selector">
              <label>Date:</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>
            <button className="btn-primary" onClick={markAllPresent}>
              Mark All Present
            </button>
          </>
        )}
      </div>

      {activeView === 'daily' && selectedClass && (
        <div className="daily-attendance">
          {classSchedule.length > 0 && (
            <div className="scheduled-practices">
              <h4>Upcoming Practices</h4>
              <div className="practice-quick-select">
                {getUpcomingPractices().map((practice) => (
                  <button
                    key={practice.date}
                    className={`practice-btn ${selectedDate === practice.date ? 'active' : ''}`}
                    onClick={() => setSelectedDate(practice.date)}
                  >
                    <div className="practice-day">{practice.dayName.substring(0, 3)}</div>
                    <div className="practice-date">{new Date(practice.date).getDate()}</div>
                    <div className="practice-time">{practice.start_time.substring(0, 5)}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="stats-bar">
            <div className="stat">
              <span className="label">Total Students</span>
              <span className="value">{attendanceData.length}</span>
            </div>
            <div className="stat">
              <span className="label">Checked In</span>
              <span className="value present">
                {attendanceData.filter(a => a.status === 'present').length}
              </span>
            </div>
            <div className="stat">
              <span className="label">Not Checked In</span>
              <span className="value">
                {attendanceData.filter(a => !a.status).length}
              </span>
            </div>
          </div>

          <div className="attendance-grid">
            {attendanceData.length === 0 ? (
              <p className="empty-state">No enrolled athletes in this class</p>
            ) : (
              attendanceData.map((athlete) => (
                <button
                  key={athlete.id}
                  className={`athlete-check-in ${athlete.status === 'present' ? 'checked-in' : ''}`}
                  onClick={() => toggleCheckIn(athlete)}
                  title={athlete.status === 'present' ? 'Click to uncheck' : 'Click to check in'}
                >
                  <div className="check-icon">
                    {athlete.status === 'present' ? '✓' : ''}
                  </div>
                  <div className="athlete-name">{athlete.name}</div>
                  {athlete.check_in_time && (
                    <div className="check-in-time">
                      {new Date(athlete.check_in_time).toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {activeView === 'reports' && selectedClass && (
        <div className="attendance-reports">
          <div className="report-header">
            <h2>Attendance Reports - {selectedClass.class_name}</h2>
          </div>

          {reports.length === 0 ? (
            <p className="empty-state">No attendance data yet</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Athlete</th>
                  <th>Total Sessions</th>
                  <th>Present</th>
                  <th>Absent</th>
                  <th>Excused</th>
                  <th>Attendance Rate</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr key={`${report.athlete_id}-${report.class_name}`}>
                    <td><strong>{report.athlete_name}</strong></td>
                    <td>{report.total_sessions}</td>
                    <td className="present">{report.present_count}</td>
                    <td className="absent">{report.absent_count}</td>
                    <td>{report.excused_count}</td>
                    <td>
                      <div className="rate-cell">
                        <span className={`rate ${getAttendanceRate(report.attendance_rate)}`}>
                          {report.attendance_rate}%
                        </span>
                        <div className="rate-bar">
                          <div
                            className="rate-fill"
                            style={{ width: `${report.attendance_rate}%` }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Check-in gate: answer the wellness questions to check in */}
      {gate && (() => {
        const allAnswered = questions.every(q => {
          if (q.qtype === 'bodymap') return true; // empty = "nothing sore" is a valid answer
          const v = gate.answers[q.id];
          return v !== undefined && v !== null && String(v).trim() !== '';
        });
        const first = (gate.athlete.name || '').split(' ')[0];
        return (
          <div className="cia-overlay" onClick={() => setGate(null)}>
            <div className="cia-modal" onClick={e => e.stopPropagation()}>
              <div className="cia-head">
                <div>
                  <h3 className="cia-title">Hi {first}! Quick check-in</h3>
                  <p className="cia-sub">Answer these to check in.</p>
                </div>
              </div>
              {questions.map(q => (
                <div key={q.id} className="cia-q">
                  <div className="cia-prompt">{q.prompt}</div>
                  {q.qtype === 'scale' && (
                    <>
                      <div className="cia-scale">
                        {[1, 2, 3, 4, 5].map(n => (
                          <button key={n} className={`cia-opt ${String(gate.answers[q.id]) === String(n) ? 'on' : ''}`}
                            onClick={() => setGateAnswer(q.id, n)}>{n}</button>
                        ))}
                      </div>
                      <div className="cia-scale-ends"><span>Low</span><span>High</span></div>
                    </>
                  )}
                  {q.qtype === 'yesno' && (
                    <div className="cia-yesno">
                      {['Yes', 'No'].map(v => (
                        <button key={v} className={`cia-opt ${gate.answers[q.id] === v ? 'on' : ''}`}
                          onClick={() => setGateAnswer(q.id, v)}>{v}</button>
                      ))}
                    </div>
                  )}
                  {q.qtype === 'text' && (
                    <input className="cia-text" value={gate.answers[q.id] || ''} placeholder="Type your answer…"
                      onChange={e => setGateAnswer(q.id, e.target.value)} />
                  )}
                  {q.qtype === 'faces' && (
                    <div className="cia-scale" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {FACE_SCALE.map(f => (
                        <button key={f.v} type="button"
                          className={`cia-opt ${String(gate.answers[q.id]) === String(f.v) ? 'on' : ''}`}
                          style={{ fontSize: '1.7rem', lineHeight: 1, padding: '6px 10px' }}
                          title={f.label}
                          onClick={() => setGateAnswer(q.id, f.v)}>
                          {f.face}
                        </button>
                      ))}
                    </div>
                  )}
                  {q.qtype === 'bodymap' && (
                    <BodyMapInput
                      value={gate.answers[q.id] || ''}
                      onChange={(arr) => setGateAnswer(q.id, arr.join(','))}
                    />
                  )}
                </div>
              ))}
              <div className="cia-actions">
                <button className="cia-submit" onClick={submitGate} disabled={!allAnswered}>
                  {allAnswered ? '✅ Check in' : 'Answer all to check in'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Admin: edit check-in questions */}
      {showEditor && (
        <CheckinQuestionsEditor onClose={() => setShowEditor(false)} onChanged={loadQuestions} />
      )}

      {/* Coach: check-in responses for the selected date */}
      {responses && (
        <div className="cia-overlay" onClick={() => setResponses(null)}>
          <div className="ciq-modal" onClick={e => e.stopPropagation()}>
            <div className="ciq-head">
              <h3>📋 Check-in responses · {selectedDate}</h3>
              <button className="ciq-close" onClick={() => setResponses(null)}>✕</button>
            </div>
            {responses.length === 0 ? (
              <p className="ciq-empty">No check-ins recorded for this day yet.</p>
            ) : (
              <div className="ciq-list">
                {responses.map(r => (
                  <div key={r.athlete_id} className="ciq-resp">
                    <div className="ciq-resp-name">{r.athlete_name}</div>
                    <div className="ciq-resp-answers">
                      {r.answers.map(a => (
                        <span key={a.question_id} className="ciq-resp-a">
                          {a.prompt}: <strong>{
                            a.qtype === 'bodymap'
                              ? (bodymapLabels(a.response).join(', ') || 'none')
                              : a.qtype === 'faces'
                                ? (FACE_SCALE.find(f => String(f.v) === String(a.response))?.face || a.response || '—')
                                : (a.response || '—')
                          }</strong>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceTracking;
